import React, { useEffect } from 'react';
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useCompany,
  usePageTitle,
  usePermission,
  useRouterOutlet,
} from '../../hooks';
import {
  CompanyType,
  CorporateCompanyPageView,
  UserPermission,
} from '../../enums';
import {
  AgencyCustomersList,
  CompanyUserList,
  ProgressIndicator,
  Show,
} from '../../components';
import { getPageTitle } from '../../utils/pages';
import { RequirePermission } from '../../security';
import { CorporareCompanyPartial } from './partials';

interface Props {
  view?: CorporateCompanyPageView;
}

const CorporateCompany: React.FC<Props> = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { showOutletOnly } = useRouterOutlet();
  const { corporateCompanyId } = useParams();
  const [searchParams] = useSearchParams();

  const { loading, item: company } = useCompany(corporateCompanyId || '');
  const viewParam = searchParams.get('view');

  const [view, setView] = React.useState<CorporateCompanyPageView>(
    CorporateCompanyPageView.CorporateCompanies
  );

  usePageTitle(getPageTitle('corporateCompany.edit', { name: company?.name }));

  useEffect(() => {
    if (company && company.type === CompanyType.Agency) {
      navigate(`/admin/agencies/${corporateCompanyId}`);
    }
    if (
      company &&
      company.type !== CompanyType.CorporateCompany &&
      company.type !== CompanyType.Agency
    ) {
      navigate(`/admin/companies/${corporateCompanyId}`);
    }
    if (!loading && !company) {
      navigate('/admin/corporate-companies');
    }
  }, [corporateCompanyId, company, navigate, loading]);

  useEffect(() => {
    if (viewParam) {
      setView(viewParam as CorporateCompanyPageView);
      navigate(`/admin/corporate-companies/${corporateCompanyId}`);
    }
  }, [corporateCompanyId, navigate, viewParam]);

  const showCorporateCompanyUserList = Boolean(
    hasPermission(UserPermission.ManageCorporateUsers) &&
      view === CorporateCompanyPageView.AssignedUsers
  );

  const showCorporateCompanyList = Boolean(
    view === CorporateCompanyPageView.CorporateCompanies
  );

  return (
    <RequirePermission oneOf={[UserPermission.ManageCorporateCompanies]}>
      {showOutletOnly || (
        <CorporareCompanyPartial
          corporateCompanyId={corporateCompanyId}
          view={view}
          setView={setView}
        >
          <>
            {loading ? (
              <ProgressIndicator />
            ) : (
              <>
                <Show if={showCorporateCompanyList}>
                  <AgencyCustomersList
                    agencyId={corporateCompanyId}
                    hiddenProperties={['shipToAmount', 'emailRecipients']}
                  />
                </Show>
                <Show if={showCorporateCompanyUserList}>
                  <CompanyUserList
                    companyId={corporateCompanyId}
                    companyType={company?.type}
                  />
                </Show>
              </>
            )}
          </>
        </CorporareCompanyPartial>
      )}

      <Outlet />
    </RequirePermission>
  );
};

export default CorporateCompany;
